import {
  adToBs,
  bsToAd,
  daysInBsMonth,
  formatIsoDate,
  parseIsoDate,
  type CalendarDate,
} from './calendar/bs'
import { CoreError } from './errors'

/** Mirrors tenant_settings.fiscal_calendar / fiscal_year_start_month / fiscal_year_start_day. */
export interface FiscalYearSettings {
  calendar: 'bs' | 'ad'
  startMonth: number
  startDay: number
}

/** Nepal: fiscal year starts 1 Shrawan (BS month 4). */
export const NEPAL_FISCAL_YEAR: FiscalYearSettings = { calendar: 'bs', startMonth: 4, startDay: 1 }

function daysInAdMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function daysInMonth(settings: FiscalYearSettings, year: number, month: number): number {
  return settings.calendar === 'bs' ? daysInBsMonth(year, month) : daysInAdMonth(year, month)
}

/** Start day clamped to the month's length (e.g. day 32 in a 31-day month). */
function startDayIn(settings: FiscalYearSettings, year: number): number {
  return Math.min(settings.startDay, daysInMonth(settings, year, settings.startMonth))
}

function validate(settings: FiscalYearSettings): void {
  if (
    settings.startMonth < 1 ||
    settings.startMonth > 12 ||
    settings.startDay < 1 ||
    settings.startDay > 32
  ) {
    throw new CoreError('invalid_input', 'fiscal year start must be a valid month/day')
  }
}

export function formatFiscalYear(startYear: number): string {
  return `${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`
}

export function parseFiscalYear(label: string): number {
  const match = /^(\d{4})\/(\d{2})$/.exec(label)
  if (!match || (Number(match[1]) + 1) % 100 !== Number(match[2])) {
    throw new CoreError('invalid_fiscal_year', `"${label}" is not like 2082/83`)
  }
  return Number(match[1])
}

/** Fiscal year label ("2082/83") for an AD date (ISO string or CalendarDate). */
export function fiscalYearOf(
  adDate: string | CalendarDate,
  settings: FiscalYearSettings = NEPAL_FISCAL_YEAR,
): string {
  validate(settings)
  const ad = typeof adDate === 'string' ? parseIsoDate(adDate) : adDate
  const local = settings.calendar === 'bs' ? adToBs(ad) : ad
  const startDay = startDayIn(settings, local.year)
  const beforeStart =
    local.month < settings.startMonth ||
    (local.month === settings.startMonth && local.day < startDay)
  return formatFiscalYear(beforeStart ? local.year - 1 : local.year)
}

/** First and last AD dates (ISO) of a fiscal year. */
export function fiscalYearRange(
  label: string,
  settings: FiscalYearSettings = NEPAL_FISCAL_YEAR,
): { start: string; end: string } {
  validate(settings)
  const startYear = parseFiscalYear(label)
  const toAd = (d: CalendarDate) => (settings.calendar === 'bs' ? bsToAd(d) : d)
  const start = toAd({
    year: startYear,
    month: settings.startMonth,
    day: startDayIn(settings, startYear),
  })
  const next = toAd({
    year: startYear + 1,
    month: settings.startMonth,
    day: startDayIn(settings, startYear + 1),
  })
  const end = new Date(Date.UTC(next.year, next.month - 1, next.day) - 86_400_000)
  return {
    start: formatIsoDate(start),
    end: formatIsoDate({
      year: end.getUTCFullYear(),
      month: end.getUTCMonth() + 1,
      day: end.getUTCDate(),
    }),
  }
}
